import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Save, Loader2, UserPlus, Trash2, ShieldAlert } from "lucide-react";

export default function Settings() {
  const [tab, setTab] = useState("Legal & Form");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [nu, setNu] = useState({ name: "", email: "", password: "", role: "member" });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data)).catch(() => toast.error("Failed to load settings"));
    api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Failed to load users"));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const createUser = async () => {
    if (!nu.email || !nu.password || !nu.name) return toast.error("Fill all fields");
    try {
      await api.post("/auth/users", nu);
      const { data } = await api.get("/auth/users");
      setUsers(data);
      setNu({ name: "", email: "", password: "", role: "member" });
      toast.success("Account created");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delUser = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try { await api.delete(`/auth/users/${id}`); setUsers(users.filter((u) => u.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!s) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="animate-fade-up">
      <h1 className={ui.pageHeader}>Settings</h1>
      <p className={`${ui.muted} mt-1 mb-6`}>Global legal text and team accounts.</p>

      <div className="flex gap-2 border-b border-slate-800 mb-6">
        {["Legal & Form", "Team"].map((t) => (
          <button key={t} data-testid={`settings-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium font-h border-b-2 -mb-px transition-colors ${tab === t ? "border-indigo-500 text-indigo-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{t}</button>
        ))}
      </div>

      {tab === "Legal & Form" && (
        <div className={`${ui.card} p-6 space-y-5`}>
          <div className="flex items-start gap-2 text-xs text-indigo-300/80 bg-indigo-950/20 border border-indigo-500/20 rounded-lg p-3">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /> These texts are inserted verbatim on every site and are never rewritten by AI.
          </div>
          <div>
            <label className={ui.label}>Disclaimer (footer)</label>
            <textarea data-testid="settings-disclaimer" className={`${ui.textarea} min-h-[140px]`} value={s.disclaimer} onChange={(e) => setS({ ...s, disclaimer: e.target.value })} />
          </div>
          <div>
            <label className={ui.label}>Consent Checkbox Text</label>
            <textarea data-testid="settings-consent" className={`${ui.textarea} min-h-[120px]`} value={s.consent_text} onChange={(e) => setS({ ...s, consent_text: e.target.value })} />
          </div>
          <div>
            <label className={ui.label}>Default Form Endpoint URL</label>
            <input data-testid="settings-endpoint" className={ui.input} value={s.form_endpoint} onChange={(e) => setS({ ...s, form_endpoint: e.target.value })} />
          </div>
          <button data-testid="save-settings" onClick={save} disabled={saving} className={ui.btnPrimary}>{saving ? <Loader2 className="w-4 h-4 spin" /> : <Save className="w-4 h-4" />} Save Settings</button>
        </div>
      )}

      {tab === "Team" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className={`${ui.card} p-6 lg:col-span-2`}>
            <h2 className={`${ui.sectionHeader} mb-4`}>Accounts</h2>
            <table className="w-full" data-testid="users-table">
              <thead><tr>{["Name", "Email", "Role", ""].map((h) => <th key={h} className="text-left text-xs font-mono uppercase text-slate-500 pb-2 border-b border-slate-800">{h}</th>)}</tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-800/60">
                    <td className="py-3 text-sm text-slate-100">{u.name}</td>
                    <td className="py-3 text-sm text-slate-300">{u.email}</td>
                    <td className="py-3"><span className={`${ui.badge} ${u.role === "admin" ? "bg-indigo-500/10 border-indigo-500/20 text-indigo-400" : "bg-slate-800 border-slate-700 text-slate-400"}`}>{u.role}</span></td>
                    <td className="py-3 text-right"><button onClick={() => delUser(u.id)} className="text-slate-600 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={`${ui.card} p-6`}>
            <h2 className={`${ui.sectionHeader} mb-4 flex items-center gap-2`}><UserPlus className="w-4 h-4 text-indigo-400" /> New Account</h2>
            <div className="space-y-3">
              <div><label className={ui.label}>Name</label><input data-testid="new-user-name" className={ui.input} value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} /></div>
              <div><label className={ui.label}>Email</label><input data-testid="new-user-email" className={ui.input} value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} /></div>
              <div><label className={ui.label}>Password</label><input data-testid="new-user-password" type="password" className={ui.input} value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} /></div>
              <div><label className={ui.label}>Role</label>
                <div className="grid grid-cols-2 gap-2">
                  {["member", "admin"].map((r) => (
                    <button key={r} data-testid={`role-${r}`} onClick={() => setNu({ ...nu, role: r })} className={`py-2 rounded-lg text-sm font-medium border capitalize ${nu.role === r ? "bg-indigo-500/15 border-indigo-500 text-indigo-300" : "bg-slate-950 border-slate-800 text-slate-400"}`}>{r}</button>
                  ))}
                </div>
              </div>
              <button data-testid="create-user" onClick={createUser} className={`${ui.btnPrimary} w-full`}><UserPlus className="w-4 h-4" /> Create Account</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
